/**
 * Tenant Upgrade API
 *
 * API functions for tenant-level upgrade operations.
 *
 * @module features/tenants/api/upgradeApi
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/upgrades/tenants';

export interface VersionDetail {
  version: string;
  description: string | null;
  is_latest: boolean;
  is_lts: boolean;
  is_security_patch: boolean;
  released_at: string | null;
  breaking_changes: string[];
  new_features: string[];
  changelog: string[];
}

export interface AvailableUpgrade {
  current_version: string;
  latest_version: string;
  available_versions: string[];
  versions_detail: VersionDetail[];
  has_upgrade: boolean;
}

export interface UpgradeValidation {
  valid: boolean;
  // Backend returns singular 'error' but we support both formats for flexibility
  error?: string;
  errors?: string[];
  warnings?: string[];
  // Backend field names
  estimated_duration_seconds?: number;
  estimated_downtime_seconds?: number;
  required_intermediates?: string[];
  migrations?: string[];
  breaking_changes?: string[];
  from_version?: string;
  to_version?: string;
}

export interface UpgradeStatus {
  id: string;
  tenant_id: string;
  from_version: string;
  to_version: string;
  status: string;
  current_step: string | null;
  progress_pct: number;
  backup_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  error_step: string | null;
}

export interface UpgradeHistoryItem {
  id: string;
  tenant_id: string;
  from_version: string;
  to_version: string;
  status: string;
  current_step: string | null;
  progress_pct: number;
  backup_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  error_step: string | null;
  initiated_by: string | null;
  rollback_by: string | null;
  rollback_at: string | null;
  rollback_reason: string | null;
  created_at: string | null;
}

export async function fetchAvailableUpgrades(authFetch: AuthFetch, tenantId: string): Promise<AvailableUpgrade> {
  const response = await authFetch(`${BASE}/${tenantId}/available`);
  if (!response.ok) throw new Error(`Failed to check available upgrades: ${response.statusText}`);
  return response.json();
}

export async function validateUpgrade(authFetch: AuthFetch, tenantId: string, targetVersion: string): Promise<UpgradeValidation> {
  const response = await authFetch(`${BASE}/${tenantId}/validate`, {
    method: 'POST',
    body: JSON.stringify({ target_version: targetVersion }),
  });
  if (!response.ok) throw new Error(`Validation failed: ${response.statusText}`);
  return response.json();
}

export async function startUpgrade(authFetch: AuthFetch, tenantId: string, targetVersion: string): Promise<UpgradeStatus> {
  const response = await authFetch(`${BASE}/${tenantId}/upgrade`, {
    method: 'POST',
    body: JSON.stringify({ target_version: targetVersion }),
  });
  if (!response.ok) {
    let errorMessage = `Failed to start upgrade: ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.error?.message) {
        errorMessage = body.error.message;
      } else if (body?.detail) {
        errorMessage = body.detail;
      } else if (typeof body === 'string') {
        errorMessage = body;
      }
    } catch {
      // If not JSON, use status text
    }
    throw new Error(errorMessage);
  }
  const data = await response.json();
  // Map backend UpgradeStatusResponse to frontend UpgradeStatus
  return {
    id: data.upgrade_id || data.id || '',
    tenant_id: data.tenant_id || tenantId,
    from_version: data.from_version || '',
    to_version: data.to_version || '',
    status: data.status || 'pending',
    current_step: data.current_step || null,
    progress_pct: data.progress_pct || 0,
    backup_id: data.backup_id || null,
    started_at: data.started_at || null,
    completed_at: data.completed_at || null,
    error_message: data.error_message || data.error || null,
    error_step: data.error_step || null,
  };
}

export async function fetchUpgradeStatus(authFetch: AuthFetch, tenantId: string): Promise<UpgradeStatus> {
  const response = await authFetch(`${BASE}/${tenantId}/status`);
  if (!response.ok) throw new Error(`Failed to fetch upgrade status: ${response.statusText}`);
  const data = await response.json();
  if (!data) throw new Error('No active upgrade');
  // Map backend UpgradeStatusResponse to frontend UpgradeStatus
  return {
    id: data.upgrade_id || data.id || '',
    tenant_id: data.tenant_id || tenantId,
    from_version: data.from_version || '',
    to_version: data.to_version || '',
    status: data.status || 'pending',
    current_step: data.current_step || null,
    progress_pct: data.progress_pct || 0,
    backup_id: data.backup_id || null,
    started_at: data.started_at || null,
    completed_at: data.completed_at || null,
    error_message: data.error_message || data.error || null,
    error_step: data.error_step || null,
  };
}

export async function rollbackUpgrade(authFetch: AuthFetch, tenantId: string, reason?: string): Promise<void> {
  const response = await authFetch(`${BASE}/${tenantId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'Manual rollback' }),
  });
  if (!response.ok) {
    let errorMessage = `Rollback failed: ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.error?.message) {
        errorMessage = body.error.message;
      } else if (body?.detail) {
        errorMessage = body.detail;
      } else if (typeof body === 'string') {
        errorMessage = body;
      }
    } catch {
      // If not JSON, use status text
    }
    throw new Error(errorMessage);
  }
}

export async function fetchUpgradeHistory(authFetch: AuthFetch, tenantId: string): Promise<UpgradeHistoryItem[]> {
  const response = await authFetch(`${BASE}/${tenantId}/history`);
  if (!response.ok) throw new Error(`Failed to fetch upgrade history: ${response.statusText}`);
  const data = await response.json();
  return data.upgrades || data;
}
